import m from "mithril";
import Layout from "./Layout.js";
import { getMithril, putMithril } from "../utils.js"; // Standardized Mithril request utilities
import showToast from "../js/toaster.js";

var EditAccount = {
    oninit: function (vnode) {
        this.accountId = m.route.param("id");
        this.customers = [];
        this.savingProducts = [];
        this.isLoading = true;
        this.isSaving = false;
        this.error = null;
        this.accountData = null;
        
        // Form data
        this.formData = {
            customer_id: "",
            saving_product_id: "",
            account_number: "",
            account_name: "",
            balance: "",
            opening_date: "",
            status: "active"
        };

        this.errors = {};

        // Load data
        if (this.accountId) {
            this.fetchAccountDetails();
            this.fetchCustomers();
            this.fetchSavingProducts();
        } else {
            this.isLoading = false;
            showToast("ID rekening tidak ditemukan", "error");
        }
    },

    fetchAccountDetails: async function () {
        try {
            const response = await getMithril(`/api/savings-accounts/${this.accountId}`, { component: this });

            if (response.success) {
                this.accountData = response.data;
                // Populate form with account data
                this.formData = {
                    customer_id: this.accountData.customer_id,
                    saving_product_id: this.accountData.saving_product_id,
                    account_number: this.accountData.account_number,
                    account_name: this.accountData.account_name,
                    balance: this.accountData.balance,
                    opening_date: this.accountData.opening_date,
                    status: this.accountData.status
                };
            } else {
                showToast("Gagal memuat detail rekening: " + (response.error || "Unknown error"), "error");
                m.route.set("/account-list");
            }
        } catch (e) {
            console.error("Account loading error:", e);
            showToast("Error memuat detail rekening: " + e.message, "error");
            m.route.set("/account-list");
        }
    },

    fetchCustomers: async function () {
        try {
            const response = await getMithril("/api/customers", { component: this });

            if (response.success) {
                this.customers = response.data || [];
            } else {
                this.error = "Failed to load customers: " + (response.error || "Unknown error");
                showToast(this.error, "error");
            }
        } catch (e) {
            this.error = "Error loading customers: " + e.message;
            console.error("Customer loading error:", e);
            showToast(this.error, "error");
        }
    },

    fetchSavingProducts: async function () {
        try {
            const response = await getMithril("/api/saving-products", { component: this });

            if (response.success) {
                this.savingProducts = response.data || [];
            } else {
                this.error = "Failed to load saving products: " + (response.error || "Unknown error");
                showToast(this.error, "error");
            }
        } catch (e) {
            this.error = "Error loading saving products: " + e.message;
            console.error("Saving products loading error:", e);
            showToast(this.error, "error");
        }
    },

    validateForm: function () {
        this.errors = {};

        if (!this.formData.customer_id) {
            this.errors.customer_id = "Nasabah harus dipilih";
        }

        if (!this.formData.saving_product_id) {
            this.errors.saving_product_id = "Produk simpanan harus dipilih";
        }

        if (!this.formData.account_number) {
            this.errors.account_number = "Nomor rekening harus diisi";
        }

        if (!this.formData.account_name) {
            this.errors.account_name = "Nama rekening harus diisi";
        }

        if (this.formData.balance === "" || isNaN(this.formData.balance) || parseFloat(this.formData.balance) < 0) {
            this.errors.balance = "Saldo harus berupa angka positif";
        }

        if (!this.formData.opening_date) {
            this.errors.opening_date = "Tanggal pembukaan harus diisi";
        }

        return Object.keys(this.errors).length === 0;
    },

    saveAccount: async function () {
        if (!this.validateForm()) {
            showToast("Silakan perbaiki kesalahan pada form", "error");
            return;
        }

        try {
            this.isSaving = true;
            m.redraw();

            const accountData = {
                customer_id: parseInt(this.formData.customer_id),
                saving_product_id: parseInt(this.formData.saving_product_id),
                account_number: this.formData.account_number.trim(),
                account_name: this.formData.account_name.trim(),
                balance: parseFloat(this.formData.balance),
                opening_date: this.formData.opening_date,
                status: this.formData.status
            };

            console.log("Updating account data:", accountData);

            const response = await putMithril(`/api/savings-accounts/${this.accountId}`, accountData, { component: this });

            if (response.success) {
                showToast("Rekening berhasil diperbarui", "success");
                m.route.set("/account-list");
            } else {
                showToast("Gagal memperbarui rekening: " + (response.error || "Unknown error"), "error");
            }
        } catch (e) {
            console.error("Account saving error:", e);
            showToast("Error memperbarui rekening: " + e.message, "error");
        } finally {
            this.isSaving = false;
            m.redraw();
        }
    },

    resetForm: function () {
        if (this.accountData) {
            this.formData = {
                customer_id: this.accountData.customer_id,
                saving_product_id: this.accountData.saving_product_id,
                account_number: this.accountData.account_number,
                account_name: this.accountData.account_name,
                balance: this.accountData.balance,
                opening_date: this.accountData.opening_date,
                status: this.accountData.status
            };
        }
        this.errors = {};
    },

    formatDate: function (dateString) {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString('id-ID');
    },

    formatCurrency: function (amount) {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    },

    view: function (vnode) {
        return [
            m("div", { class: "max-w-7xl mx-auto px-4 sm:px-6 md:px-8" }, [
                m("div", { class: "md:flex md:items-center md:justify-between" }, [
                    m("div", { class: "flex-1 min-w-0" }, [
                        m("h2", { class: "text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate" }, 
                            "Edit Rekening Simpanan"
                        )
                    ]),
                    m("div", { class: "mt-4 flex md:mt-0 md:ml-4" }, [
                        m("button", {
                            type: "button",
                            class: "ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
                            onclick: () => m.route.set("/account-list")
                        }, "Kembali")
                    ])
                ]),

                m("div", { class: "mt-8 bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg" }, [
                    this.isLoading ? 
                        m("div", { class: "px-4 py-5 sm:p-6" }, [
                            m("div", { class: "text-center py-12" }, [
                                m("div", { class: "inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4" }),
                                m("p", { class: "text-gray-600 dark:text-gray-400" }, "Memuat data rekening...")
                            ])
                        ]) : 
                        m("div", { class: "px-4 py-5 sm:p-6" }, [
                            this.error ? 
                                m("div", { class: "rounded-md bg-red-50 dark:bg-red-900 p-4 mb-6" }, [
                                    m("div", { class: "flex" }, [
                                        m("div", { class: "flex-shrink-0" }, [
                                            m("svg", { class: "h-5 w-5 text-red-400", viewBox: "0 0 20 20", fill: "currentColor" }, [
                                                m("path", { "fill-rule": "evenodd", d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z", "clip-rule": "evenodd" })
                                            ])
                                        ]),
                                        m("div", { class: "ml-3" }, [
                                            m("h3", { class: "text-sm font-medium text-red-800 dark:text-red-200" }, "Error"),
                                            m("div", { class: "mt-2 text-sm text-red-700 dark:text-red-300" }, this.error)
                                        ])
                                    ])
                                ]) : null,

                            m("form", { 
                                onsubmit: (e) => {
                                    e.preventDefault();
                                    this.saveAccount();
                                }
                            }, [
                                m("div", { class: "grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" }, [
                                    // Customer selection
                                    m("div", { class: "sm:col-span-3" }, [
                                        m("label", { for: "customer_id", class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, 
                                            "Pilih Nasabah"
                                        ),
                                        m("select", {
                                            id: "customer_id",
                                            class: `mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                                                this.errors.customer_id ? "border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500" : ""
                                            }`,
                                            value: this.formData.customer_id,
                                            onchange: (e) => {
                                                this.formData.customer_id = e.target.value;
                                                // Auto-fill account name when customer is selected
                                                if (e.target.value) {
                                                    const selectedCustomer = this.customers.find(c => c.id == e.target.value);
                                                    if (selectedCustomer && !this.formData.account_name) {
                                                        this.formData.account_name = selectedCustomer.nama_nasabah;
                                                    }
                                                }
                                            }
                                        }, [
                                            m("option", { value: "" }, "Pilih Nasabah"),
                                            this.customers.map(customer => 
                                                m("option", { 
                                                    value: customer.id,
                                                    selected: customer.id == this.formData.customer_id
                                                }, `${customer.nama_nasabah} (${customer.no_registrasi || 'No Reg'})`)
                                            )
                                        ]),
                                        this.errors.customer_id ? 
                                            m("p", { class: "mt-2 text-sm text-red-600 dark:text-red-400" }, this.errors.customer_id) : null
                                    ]),

                                    // Saving product selection
                                    m("div", { class: "sm:col-span-3" }, [
                                        m("label", { for: "saving_product_id", class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, 
                                            "Pilih Produk Simpanan"
                                        ),
                                        m("select", {
                                            id: "saving_product_id",
                                            class: `mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                                                this.errors.saving_product_id ? "border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500" : ""
                                            }`,
                                            value: this.formData.saving_product_id,
                                            onchange: (e) => {
                                                this.formData.saving_product_id = e.target.value;
                                            }
                                        }, [
                                            m("option", { value: "" }, "Pilih Produk"),
                                            this.savingProducts.map(product => 
                                                m("option", { 
                                                    value: product.id,
                                                    selected: product.id == this.formData.saving_product_id
                                                }, `${product.name} (${product.code})`)
                                            )
                                        ]),
                                        this.errors.saving_product_id ? 
                                            m("p", { class: "mt-2 text-sm text-red-600 dark:text-red-400" }, this.errors.saving_product_id) : null
                                    ]),

                                    // Account number
                                    m("div", { class: "sm:col-span-3" }, [
                                        m("label", { for: "account_number", class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, 
                                            "Nomor Rekening"
                                        ),
                                        m("input", {
                                            type: "text",
                                            id: "account_number",
                                            class: `mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                                                this.errors.account_number ? "border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500" : ""
                                            }`,
                                            value: this.formData.account_number,
                                            oninput: (e) => {
                                                this.formData.account_number = e.target.value;
                                            }
                                        }),
                                        this.errors.account_number ? 
                                            m("p", { class: "mt-2 text-sm text-red-600 dark:text-red-400" }, this.errors.account_number) : null
                                    ]),

                                    // Account name
                                    m("div", { class: "sm:col-span-3" }, [
                                        m("label", { for: "account_name", class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, 
                                            "Nama Rekening"
                                        ),
                                        m("input", {
                                            type: "text",
                                            id: "account_name",
                                            class: `mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                                                this.errors.account_name ? "border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500" : ""
                                            }`,
                                            value: this.formData.account_name,
                                            oninput: (e) => {
                                                this.formData.account_name = e.target.value;
                                            }
                                        }),
                                        this.errors.account_name ? 
                                            m("p", { class: "mt-2 text-sm text-red-600 dark:text-red-400" }, this.errors.account_name) : null
                                    ]),

                                    // Balance
                                    m("div", { class: "sm:col-span-3" }, [
                                        m("label", { for: "balance", class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, 
                                            "Saldo (Rp)"
                                        ),
                                        m("input", {
                                            type: "number",
                                            id: "balance",
                                            class: `mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                                                this.errors.balance ? "border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500" : ""
                                            }`,
                                            value: this.formData.balance,
                                            oninput: (e) => {
                                                this.formData.balance = e.target.value;
                                            }
                                        }),
                                        this.errors.balance ? 
                                            m("p", { class: "mt-2 text-sm text-red-600 dark:text-red-400" }, this.errors.balance) : null
                                    ]),

                                    // Opening date
                                    m("div", { class: "sm:col-span-3" }, [
                                        m("label", { for: "opening_date", class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, 
                                            "Tanggal Pembukaan"
                                        ),
                                        m("input", {
                                            type: "date",
                                            id: "opening_date",
                                            class: `mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                                                this.errors.opening_date ? "border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500" : ""
                                            }`,
                                            value: this.formData.opening_date,
                                            oninput: (e) => {
                                                this.formData.opening_date = e.target.value;
                                            }
                                        }),
                                        this.errors.opening_date ? 
                                            m("p", { class: "mt-2 text-sm text-red-600 dark:text-red-400" }, this.errors.opening_date) : null
                                    ]),

                                    // Status
                                    m("div", { class: "sm:col-span-3" }, [
                                        m("label", { for: "status", class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, 
                                            "Status"
                                        ),
                                        m("select", {
                                            id: "status",
                                            class: "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm",
                                            value: this.formData.status,
                                            onchange: (e) => {
                                                this.formData.status = e.target.value;
                                            }
                                        }, [
                                            m("option", { value: "active" }, "Aktif"),
                                            m("option", { value: "inactive" }, "Tidak Aktif")
                                        ])
                                    ])
                                ]),

                                // Form actions
                                m("div", { class: "mt-8 border-t border-gray-200 dark:border-gray-700 pt-5" }, [
                                    m("div", { class: "flex justify-end space-x-3" }, [
                                        m("button", {
                                            type: "button",
                                            class: "bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600",
                                            onclick: () => this.resetForm()
                                        }, "Reset"),
                                        m("button", {
                                            type: "submit",
                                            disabled: this.isSaving,
                                            class: "inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                        }, this.isSaving ? "Menyimpan..." : "Perbarui Rekening")
                                    ])
                                ])
                            ])
                        ])
                ])
            ])
        ];
    }
};

export default EditAccount;